import * as RadixContextMenu from '@radix-ui/react-context-menu';
import { cn } from '@/shared/lib/cn';
import type { ComponentProps } from 'react';

/**
 * Sağ tık menüsü.
 *
 * `modal` varsayılan olarak KAPALI. Radix açık menüde `pointer-events: none` uygulayıp
 * dışarıdaki her şeyi etkisizleştiriyor; menü açıkken başka bir hücreye sağ tıklandığında
 * olay hedefe hiç ulaşmıyor, menü eski hücrenin bilgisiyle yeniden açılıyordu ("… ile
 * filtrele" yanlış kolonu gösteriyordu). Modal olmayan menüde ikinci sağ tık doğrudan
 * yeni hedefe gider. Menü dışına tıklayınca kapanma davranışı değişmiyor.
 */
export function ContextMenu({ modal = false, ...props }: ComponentProps<typeof RadixContextMenu.Root>) {
  return <RadixContextMenu.Root modal={modal} {...props} />;
}
export const ContextMenuTrigger = RadixContextMenu.Trigger;
export const ContextMenuSeparator = () => <RadixContextMenu.Separator className="bg-border my-1 h-px" />;

export function ContextMenuContent({ className, ...props }: ComponentProps<typeof RadixContextMenu.Content>) {
  return (
    <RadixContextMenu.Portal>
      <RadixContextMenu.Content
        className={cn(
          'border-border bg-bg-elevated text-fg z-50 min-w-[11rem] rounded-md border p-1 text-sm shadow-[var(--shadow)]',
          className,
        )}
        {...props}
      />
    </RadixContextMenu.Portal>
  );
}

export function ContextMenuItem({ className, ...props }: ComponentProps<typeof RadixContextMenu.Item>) {
  return (
    <RadixContextMenu.Item
      className={cn(
        'data-[highlighted]:bg-bg-hover flex cursor-default items-center gap-2 rounded px-2 py-1.5 outline-none select-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    />
  );
}

export const ContextMenuSub = RadixContextMenu.Sub;
export const ContextMenuLabel = ({ className, ...p }: ComponentProps<typeof RadixContextMenu.Label>) => (
  <RadixContextMenu.Label
    className={cn('text-fg-subtle px-2 py-1.5 text-xs font-semibold tracking-wide uppercase', className)}
    {...p}
  />
);

export function ContextMenuSubTrigger({
  className,
  ...props
}: ComponentProps<typeof RadixContextMenu.SubTrigger>) {
  return (
    <RadixContextMenu.SubTrigger
      className={cn(
        'data-[highlighted]:bg-bg-hover data-[state=open]:bg-bg-hover flex cursor-default items-center gap-2 rounded px-2 py-1.5 outline-none select-none',
        // Radix ok işaretini çağıran koymuyor; sağa yaslı ► her alt menüde aynı görünsün.
        "after:text-fg-subtle after:ml-auto after:content-['›']",
        className,
      )}
      {...props}
    />
  );
}

export function ContextMenuSubContent({
  className,
  ...props
}: ComponentProps<typeof RadixContextMenu.SubContent>) {
  return (
    <RadixContextMenu.Portal>
      <RadixContextMenu.SubContent
        className={cn(
          'border-border bg-bg-elevated text-fg z-50 min-w-[11rem] rounded-md border p-1 text-sm shadow-[var(--shadow)]',
          className,
        )}
        {...props}
      />
    </RadixContextMenu.Portal>
  );
}
